import { LitElement, css, html } from "lit-element";
import "../components/target-link";
import { checkLoginStatus } from "@/utils/auth";
import GameApp from "../../index";
import { gameConfig } from "@/config";

class Login extends LitElement {
  errors: any = {};
  loggedIn: boolean | null = null; // Use null to indicate the initial unknown state
  loading = true; // Add a loading state
  discordLoggingIn = false; // Add a state for Discord login in progress

  static get properties() {
    return {
      errors: { type: Object },
      loggedIn: { type: Boolean },
      loading: { type: Boolean },
      discordLoggingIn: { type: Boolean },
    };
  }

  protected createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.isLoggedIn();
    this.handleDiscordCallback(); // Check for code in the URL
  }

  handleDiscordCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code && !window.location.pathname.includes("activate")) {
      this.discordLoggingIn = true; // Set Discord login state to true

      fetch(`${process.env.BASE_API_URL}/discord`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ code }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            this.errors = data;
            this.discordLoggingIn = false;
          } else {
            localStorage.setItem(
              "tam_user_id",
              JSON.stringify(data.user.userId)
            );
            localStorage.setItem(
              "tam_user_role",
              JSON.stringify(data.user.roles)
            );
            window.location.href = "/"; // Redirect to home or game
          }
        })
        .catch((error) => {
          console.error("Error during Discord login:", error);
          alert("An error occurred during login. Please try again later.");
        });
    }
  }

  async isLoggedIn() {
    try {
      const user = (await checkLoginStatus(
        `${process.env.BASE_API_URL}/me`
      )) as any;

      if (user.loggedIn) {
        localStorage.setItem(
          "tam_user",
          JSON.stringify({
            look: user.look || gameConfig.default.look,
            coins: user.coins,
          })
        );
        await GameApp.wsClient.connect();

        GameApp.showDashboard();
        GameApp.wsClient.sendMessage(
          JSON.stringify({
            action: "join_room",
            arguments: {
              roomId: "1",
            },
          })
        );
      }

      this.loggedIn = user.loggedIn;
    } catch (error) {
      console.error("Error checking login status:", error);
      this.loggedIn = false;
    } finally {
      this.loading = false;
    }
  }

  clearError(field) {
    if (this.errors.field === field || this.errors.field === "email_password") {
      this.errors = {};
    }
  }

  render() {
    if (this.loading || this.discordLoggingIn) {
      return html`
        <div class="loading-container">
          <p>
            ${this.discordLoggingIn
              ? html`<img
                    src="public/gui/loadingduck.png"
                    style="width: 100px; height: 100px; "
                    alt="Loading"
                  />

                  Logging in with Discord`
              : "Loading..."}
          </p>
        </div>
      `;
    }

    if (this.loggedIn) {
      GameApp.startGame();

      const gameElement = document.querySelector("#game") as HTMLElement;
      const appContainer = document.querySelector(
        "app-container"
      ) as HTMLElement;

      if (gameElement) {
        gameElement.style.display = "block";
      }

      if (appContainer) {
        appContainer.style.display = "none";
      }
    }

    return html`
      <div class="auth-container">
        <h1>Login</h1>

        <form @submit="${this.handleSubmit}">
          ${this.errors.field === "top" ||
          this.errors.field === "email_password"
            ? html`<div class="form-error-message is-top">
                ${this.errors.error}
              </div>`
            : ""}

          <div
            class="form-group ${this.errors.field === "email" ||
            this.errors.field === "email_password"
              ? "has-error"
              : ""}"
          >
            <label for="email">Email Address</label>
            <input
              type="text"
              name="email"
              class="${this.errors.field === "email" ? "error" : ""}"
              @input="${() => this.clearError("email")}"
            />

            ${this.errors.field === "email"
              ? html`<span class="form-error-message"
                  >${this.errors.error}</span
                >`
              : ""}
          </div>

          <div
            class="form-group ${this.errors.field === "password" ||
            this.errors.field === "email_password"
              ? "has-error"
              : ""}"
          >
            <label for="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              class="${this.errors.field === "password" ? "error" : ""}"
              @input="${() => this.clearError("password")}"
            />
            ${this.errors.field === "password"
              ? html`<span class="form-error-message"
                  >${this.errors.error}</span
                >`
              : ""}
          </div>

          <a href="/forgot-password" class="form-forgot-link">
            Forgot password?
          </a>

          <div class="form-submit-section">
            <button type="submit" class="form-submit form-button">
              <span>Sign in</span>
            </button>
            Or
            <button
              @click="${this.handleDiscordLogin}"
              class="form-discord form-button"
            >
              <span class="hidden">Sign in with Discord</span>
            </button>
          </div>
        </form>

        <p class="form-message">
          Ready to join the fun?
          <a href="/register" class="signup-link">Sign up</a>
        </p>
      </div>
    `;
  }

  handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const email = formData.get("email");
    const password = formData.get("password");

    const requestData = {
      email,
      password,
    };

    fetch(`${process.env.BASE_API_URL}/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json", // ✅ This is missing in your current code
      },
      body: JSON.stringify(requestData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          this.errors = data;
        } else {
          localStorage.setItem("tam_user_id", JSON.stringify(data.user.userId));
          localStorage.setItem(
            "tam_user_role",
            JSON.stringify(data.user.roles)
          );
          window.location.href = "/";
        }
      })
      .catch((error) => {
        console.error("Error during login:", error);
        alert("An error occurred during login. Please try again later.");
      });
  }

  handleDiscordLogin(event) {
    event.preventDefault();
    this.discordLoggingIn = true; // Set Discord login state to true

    const clientId = "1334294322203459715";
    const redirectUri = encodeURIComponent(`${process.env.ALLOW_ORIGIN}`);
    const scope = "identify email";

    window.location.href = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;
  }
}

customElements.define("login-page", Login);
